import {
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNotificationRightOffset } from "../../../notification-provider";
import { AiSlidePanelContext } from "../contexts/ai-slide-panel-context";
import * as styles from "../styles/ai-slide-panel-layout.module.css";

/**
 * Shared grid layout for a right-hand slide panel that pushes the
 * main content column instead of overlaying it. Used by:
 *   - the merchant app shell (legacy `GlobalAI` slide-out)
 *   - the onboarding shell (`GlobalAskRedo` slide-out)
 *   - the new Ask Redo slide-out
 *
 * Open/close state is read from `useAiSlidePanel()` by default (the
 * legacy context), but consumers that own their own panel state can
 * pass `isOpen` as a prop. When the prop is supplied, the legacy
 * context is NOT subscribed — that lets self-contained callers mount
 * without an `AiSlidePanelProvider` at all and avoids the case where
 * a child re-renders just because the legacy context happened to
 * change.
 *
 * Notification offset coordination is refcounted via
 * `useNotificationRightOffset`, so the merchant app's two layouts
 * (legacy + Ask Redo) can both be open and the close-callback of
 * one doesn't wipe the other's offset.
 *
 * Behaviour otherwise matches the prior implementation: resize
 * divider, smooth-transition column expansion, focus-on-open input
 * handoff.
 */
interface AiSlidePanelLayoutProps {
  children: ReactNode;
  panel: ReactNode;
  panelWidth?: "sm" | "md" | "lg";
  noRounding?: boolean;
  /**
   * Override the open/closed state. When omitted, the layout reads
   * from `useAiSlidePanel()` (the legacy context). When provided,
   * the legacy context is bypassed entirely so the layout works
   * outside an `AiSlidePanelProvider` and isolated from unrelated
   * legacy state changes.
   */
  isOpen?: boolean;
}

const MIN_PANEL_WIDTH = 324;
const MAX_PANEL_WIDTH = 548;
const DEFAULT_WIDTHS = { sm: 364, md: 548, lg: 548 };
const TRANSITION_MS = 300;

export const AiSlidePanelLayout = memo(function AiSlidePanelLayout({
  children,
  panel,
  panelWidth = "sm",
  noRounding = false,
  isOpen: isOpenProp,
}: AiSlidePanelLayoutProps) {
  // Read the legacy context conditionally without violating the
  // rules of hooks: `useContext(...)` is called unconditionally,
  // but we only consult its value when the caller didn't supply
  // an `isOpen` prop. When provider-less callers pass `isOpen`,
  // the context value is `undefined` and we never use it.
  const legacyContext = useContext(AiSlidePanelContext);
  const isOpen = isOpenProp ?? legacyContext?.isOpen ?? false;
  // `setPanelWidth` exists only on the legacy context; outside an
  // `AiSlidePanelProvider` it's a no-op. Wrapped in useHandler-style
  // stable identity so the width-sync effect doesn't re-fire on
  // unrelated re-renders.
  const reportLegacyWidth = useCallback(
    (w: number) => legacyContext?.setPanelWidth(w),
    [legacyContext],
  );

  const panelRef = useRef<HTMLDivElement>(null);
  const [currentPanelWidth, setCurrentPanelWidth] = useState<number>(
    DEFAULT_WIDTHS[panelWidth],
  );
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartX = useRef<number>(0);
  const resizeStartWidth = useRef<number>(0);

  // Keep the legacy context's width in sync — single source of
  // truth for consumers (e.g. `SlideOutChatModalGlobal`) that read
  // panel width to position themselves.
  useEffect(() => {
    reportLegacyWidth(currentPanelWidth);
  }, [currentPanelWidth, reportLegacyWidth]);

  // Refcounted claim on the notification offset. When this layout
  // is closed we pass `undefined` to release.
  useNotificationRightOffset(isOpen ? currentPanelWidth : undefined);

  // Focus the first input/textarea inside the panel when it opens.
  // We hook off `transitionend` rather than a magic-number
  // setTimeout so the focus moves exactly when the column finishes
  // expanding — works regardless of the CSS transition duration.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const panelEl = panelRef.current;
    if (!panelEl) {
      return;
    }
    let cancelled = false;
    const focus = () => {
      if (cancelled) {
        return;
      }
      const input = panelEl.querySelector("input, textarea");
      if (
        input instanceof HTMLInputElement ||
        input instanceof HTMLTextAreaElement
      ) {
        input.focus();
      }
    };
    // Some browsers won't emit `transitionend` if the panel was
    // already at its target width (no animation runs). Fall back
    // to a timer matching the CSS transition so focus still lands.
    const fallback = setTimeout(focus, TRANSITION_MS + 50);
    const onTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName === "grid-template-columns") {
        clearTimeout(fallback);
        focus();
      }
    };
    const grid = panelEl.parentElement;
    grid?.addEventListener("transitionend", onTransitionEnd);
    return () => {
      cancelled = true;
      clearTimeout(fallback);
      grid?.removeEventListener("transitionend", onTransitionEnd);
    };
  }, [isOpen]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      resizeStartX.current = e.clientX;
      resizeStartWidth.current = currentPanelWidth;
    },
    [currentPanelWidth],
  );

  useEffect(() => {
    if (!isResizing) {
      return undefined;
    }
    const onMove = (e: MouseEvent) => {
      const deltaX = resizeStartX.current - e.clientX;
      setCurrentPanelWidth(
        Math.min(
          MAX_PANEL_WIDTH,
          Math.max(MIN_PANEL_WIDTH, resizeStartWidth.current + deltaX),
        ),
      );
    };
    const onUp = () => setIsResizing(false);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing]);

  return (
    <div
      className={`${styles.layoutContainer} ${isOpen ? styles.layoutContainerOpen : ""} ${isResizing ? styles.layoutContainerResizing : ""} ${noRounding ? styles.layoutContainerNoRounding : ""}`}
      style={{
        gridTemplateColumns: isOpen
          ? `1fr 6px ${currentPanelWidth}px`
          : "1fr 0px 0px",
      }}
    >
      <div className={styles.mainContent}>{children}</div>

      {isOpen && (
        <div className={styles.resizeDivider} onMouseDown={handleMouseDown} />
      )}

      <div
        className={styles.slidePanel}
        ref={panelRef}
        {...(!isOpen && { inert: "" })}
      >
        {panel}
      </div>
    </div>
  );
});

// Re-export the legacy hook for callers that still drive open/close
// state through the singleton context.

export { useAiSlidePanel } from "../contexts/ai-slide-panel-context";
